import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

// ── Re-export V4/V5 shared interfaces ─────────────────────────
export {
  V4PartnerStats,
  V4ShipmentStats,
  V4UpcomingShipment,
  V4TransportUnitStats,
  V4TallyStats,
  V4VolumeTrendPoint,
  V4ProductVolume,
  V4LoadingSiteStats,
  V4SubscriptionInfo,
  V4RecentTransportUnit,
  InventorySnapshot,
} from '../../dashboard-v4/services/dashboard-v4.service';

export {
  V5AgingBucket,
  V5CurrencyTotals,
  V5FinancialHealth,
  V5TopDiscrepancy,
  V5ReconciliationStatus,
  V5ProcessingStats,
} from '../../dashboard-v5/services/dashboard-v5.service';

import {
  V4PartnerStats,
  V4ShipmentStats,
  V4TransportUnitStats,
  V4TallyStats,
  V4LoadingSiteStats,
  V4SubscriptionInfo,
  V4RecentTransportUnit,
} from '../../dashboard-v4/services/dashboard-v4.service';

import {
  V5FinancialHealth,
  V5ReconciliationStatus,
  V5ProcessingStats,
} from '../../dashboard-v5/services/dashboard-v5.service';

// ── New V6 interfaces ──────────────────────────────────────────

/** Same shape as InventorySnapshot but with the raw CBM totals the backend actually sends
 *  (V6 embeds this server-side now — no separate /inventory/summary call needed like V5 did). */
export interface V6InventorySummary {
  availableCbm: number;
  /** 2026-09-15 — real stock left (open batches after sales/mill use/holds + units not yet in a
   *  batch). Command Center's "Stock on hand" uses this; availableCbm over-counts once stock is used. */
  stockOnHandCbm?: number;
  /** 2026-09-30: really in stock (held + free) and the held part — Command Center shows
   *  "In stock" with "Held · Free" under it. stockOnHandCbm above is the free part. */
  physicalStockCbm?: number;
  heldStockCbm?: number;
  /** 2026-10-06: holds beyond what a batch contains. */
  overHeldCbm?: number;
  overHeldLotCount?: number;
  negativeStockCbm?: number;
  negativeLotCount?: number;
  /** 2026-09-27 — tally corrections waiting for a person to Apply/Dismiss (Inventory → In & Out). */
  pendingTallySyncReviews?: number;
  availableTuCount: number;
  inProcessCbm: number;
  inShipmentCbm: number;
  shippedCbmThisMonth: number;
  openLotsCbm?: number;
  openLotsCount?: number;
  bySite?: any[];
}

export interface V6OperatorLoad {
  operatorName: string;
  activeTaskCount: number;
}

export interface V6MillBoard {
  queuedTasks: number;
  inProgressTasks: number;
  completedToday: number;
  totalMachines: number;
  busyMachines: number;
  topOperators: V6OperatorLoad[];
  /** Running jobs first, then waiting ones; at most 4. */
  activeRuns?: V6ActiveRun[];
}

export interface V6ActiveRun {
  encodedId: string;
  runCode: string;
  status: string;
  inputProductName: string | null;
  outputProductName: string | null;
  inputCbm: number | null;
  millSiteName: string | null;
}

export interface V6MyMillTask {
  encodedId: string;
  runCode: string;
  status: string;
  priority: string;
  inputProductName: string;
  outputProductName: string;
  millSiteName: string;
  /** 2026-09-17 — true once I accepted this mill task. */
  accepted?: boolean;
}

export interface V6ActivityItem {
  action: string;
  permissionCode: string;
  targetUserName: string;
  doneByName: string;
  doneAt: string;
}

export interface V6DashboardStats {
  partners: V4PartnerStats;
  shipments: V4ShipmentStats;
  transportUnits: V4TransportUnitStats;
  tally: V4TallyStats;
  loadingSites: V4LoadingSiteStats[];
  subscription: V4SubscriptionInfo;
  recentTransportUnits: V4RecentTransportUnit[];
  inventory: V6InventorySummary | null;
  financialHealth: V5FinancialHealth | null;
  reconciliation: V5ReconciliationStatus | null;
  processing: V5ProcessingStats | null;
  millBoard: V6MillBoard | null;
  myTasks: V6MyMillTask[] | null;
  recentActivity: V6ActivityItem[] | null;
}

@Injectable({ providedIn: 'root' })
export class DashboardV6Service {
  private readonly base = `${environment.apiUrl}api/v1/dashboard-v6`;

  constructor(private http: HttpClient) {}

  getStats(from: string, to: string): Observable<V6DashboardStats> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<V6DashboardStats>(`${this.base}/stats`, { params });
  }
}
